/**
 * Custom hook that encapsulates all research preferences store actions.
 * Provides a clean abstraction over direct store access, following the pattern
 * of extracting reusable logic into custom hooks.
 *
 * @param sessionType - Session type identifier for namespaced store access
 * @returns Object containing categorized action methods for research preferences
 */

import { useResearchPreferencesStore } from "@/stores/research-preferences-store"

export function useResearchPreferencesActions(sessionType: string) {
  const researchPrefsStore = useResearchPreferencesStore(sessionType)

  // Report-related actions
  const reportActions = {
    setReportLength: researchPrefsStore((state) => state.setReportLength),
    setCustomReportLength: researchPrefsStore((state) => state.setCustomReportLength),
    setReportFormatText: researchPrefsStore((state) => state.setReportFormatText),
    setReportFormatFile: researchPrefsStore((state) => state.setReportFormatFile),
    setReportFormatSource: researchPrefsStore((state) => state.setReportFormatSource),
  }

  // Source-related actions
  const sourceActions = {
    setSources: researchPrefsStore((state) => state.setSources),
    setSourceUrls: researchPrefsStore((state) => state.setSourceUrls),
  }

  // Language-related actions
  const languageActions = {
    setLanguage: researchPrefsStore((state) => state.setLanguage),
    addLanguage: researchPrefsStore((state) => state.addLanguage),
    deleteLanguage: researchPrefsStore((state) => state.deleteLanguage),
  }

  // Research configuration actions
  const configActions = {
    setResearchDepth: researchPrefsStore((state) => state.setResearchDepth),
    setCountryCodes: researchPrefsStore((state) => state.setCountryCodes),
    setFileHandlingStrategy: researchPrefsStore((state) => state.setFileHandlingStrategy),
  }

  return {
    reportActions,
    sourceActions,
    languageActions,
    configActions,
  }
}
